import React, { useReducer, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// UI Components
import DemographicsForm from './DemographicsForm';
import QuestionForm from './QuestionForm';
import ProgressBar from './ProgressBar';
import LoadingDisplay from './LoadingDisplay'; 
import ErrorDisplay from './ErrorDisplay';

// Utils and Services
import { initializeQuestions, getShuffledQuestions, getRawQuestionsData, Question } from '../utils/questionService';
import { saveSession, loadSession, getCurrentSessionId } from '../utils/storage';
import { calculateResults } from '../utils/calculations';
import { buildSessionQuestions } from '../utils/sessionQuestions';
import { validateData } from '../utils/runtimeValidation';
import { ResultService } from '../services/resultService';

// State, Reducer, and Actions
interface TestState {
  step: string;
  questions: Question[];
  currentQuestionIndex: number;
  demographics: Record<string, unknown> | null;
  error: string | null;
  loading: boolean;
}

type TestAction =
  | { type: 'INITIALIZE'; payload: { questions: Question[]; demographics: Record<string, unknown> | null; currentQuestionIndex: number; step: string } }
  | { type: 'ANSWER_QUESTION'; payload: { questionId: string; answer: unknown } }
  | { type: 'PREVIOUS_QUESTION' }
  | { type: 'SUBMIT_DEMOGRAPHICS'; payload: Record<string, unknown> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'FINISH_TEST' };

function testReducer(state: TestState, action: TestAction): TestState {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        questions: action.payload.questions,
        demographics: action.payload.demographics,
        currentQuestionIndex: action.payload.currentQuestionIndex,
        step: action.payload.step,
      };
    case 'ANSWER_QUESTION':
      const newQuestions = state.questions.map(q =>
        q.id === action.payload.questionId ? { ...q, answer: action.payload.answer } : q
      );
      const isLastQuestion = state.currentQuestionIndex >= state.questions.length - 1;
      return {
        ...state,
        questions: newQuestions,
        step: isLastQuestion ? 'finishing' : state.step,
        currentQuestionIndex: isLastQuestion ? state.currentQuestionIndex : state.currentQuestionIndex + 1,
      };
    case 'PREVIOUS_QUESTION':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, state.currentQuestionIndex - 1),
      };
    case 'SUBMIT_DEMOGRAPHICS':
      return {
        ...state,
        demographics: action.payload,
        step: 'test',
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, step: 'error', loading: false };
    case 'FINISH_TEST':
      return { ...state, step: 'finishing', loading: true };
    default:
      return state;
  }
}

// --- Вспомогательная функция для формирования финального результата ---
function buildFinalResults(sessionId: string, sessionQuestions: Question[], demographics: Record<string, unknown>, calculatedData: any) {
  const cleanDemographics = (demographics && typeof demographics === 'object') ? demographics : {};
  return {
    ...calculatedData,
    id: sessionId,
    timestamp: new Date().toISOString(),
    demographics: cleanDemographics,
    sessionQuestions,
  };
}

// --- Асинхронный флоу завершения теста ---
async function finishTestFlow({ state, sessionId, navigate, dispatch }: { state: TestState; sessionId: string; navigate: (path: string) => void; dispatch: React.Dispatch<TestAction> }) {
  try {
    const { questions, demographics } = state;
    console.log('[finishTestFlow] Начинаю расчёт результатов для sessionId:', sessionId);
    const sessionQuestions = buildSessionQuestions(questions);
    let calculatedData: any;
    try {
      calculatedData = await calculateResults(sessionQuestions);
      console.log('[finishTestFlow] Расчёт результатов завершён:', calculatedData);
    } catch (e) {
      console.error('[finishTestFlow] Ошибка при расчёте результатов:', e.message);
      dispatch({ type: 'SET_ERROR', payload: `Ошибка при расчёте результатов: ${e.message}` });
      return;
    }
    const finalResults = buildFinalResults(sessionId, sessionQuestions, demographics || {}, calculatedData);
    console.log('[finishTestFlow] Финальный объект результата:', finalResults);
    try {
      validateData('result_test.schema.json', finalResults);
      console.log('[finishTestFlow] Валидация результата прошла успешно.');
    } catch (e) {
      console.error('[finishTestFlow] Ошибка валидации результата:', e.message, finalResults);
      dispatch({ type: 'SET_ERROR', payload: `Ошибка валидации результата: ${e.message}` });
      return;
    }
    try {
      await ResultService.saveResult(finalResults);
      console.log('[finishTestFlow] saveResults успешно завершён.');
    } catch (e) {
      console.error('[finishTestFlow] Ошибка при локальном сохранении результата:', e.message);
      dispatch({ type: 'SET_ERROR', payload: `Ошибка при локальном сохранении результата: ${e.message}` });
      return;
    }
    try {
      // await saveTestResult(sessionId, sessionId, finalResults, sessionQuestions, demographics);
      console.log('[finishTestFlow] saveTestResult успешно завершён. Переход на страницу результатов...');
    } catch (e) {
      console.error('[finishTestFlow] Ошибка при сохранении результата в Firebase:', e.message);
      dispatch({ type: 'SET_ERROR', payload: `Ошибка при сохранении результата в Firebase: ${e.message}` });
      return;
    }
    navigate(`/results/${sessionId}`);
  } catch (error) {
    console.error('[finishTestFlow] CRITICAL ERROR:', error);
    dispatch({ type: 'SET_ERROR', payload: `Произошла критическая ошибка: ${error.message}` });
  }
}

// --- Централизованная функция для получения или создания sessionId ---
function getOrCreateSessionId() {
  let sessionId = getCurrentSessionId();
  if (!sessionId) {
    sessionId = uuidv4();
    localStorage.setItem('psyco_current_session_id', sessionId);
  }
  return sessionId;
}

// --- Кастомный хук для автоматического сохранения сессии ---
function useSessionSaver(state: TestState) {
  useEffect(() => {
    if (state.step === 'test' || state.step === 'demographics') {
      const sessionData = {
        questions: state.questions.filter(q => q.answer !== undefined),
        demographics: state.demographics,
        currentQuestionIndex: state.currentQuestionIndex,
        step: state.step,
      };
      saveSession(sessionData);
    }
  }, [state.questions, state.demographics, state.currentQuestionIndex, state.step]);
}

// --- Компонент для централизованного отображения ошибок теста ---
function TestErrorDisplay({ error }: { error: string }) {
  if (!error) return null;
  return <ErrorDisplay error={error} />;
}

const TestContainer = () => {
  const initialState: TestState = {
    step: 'loading',
    questions: [],
    currentQuestionIndex: 0,
    demographics: null,
    error: null,
    loading: false,
  };
  const [state, dispatch] = useReducer(testReducer, initialState);
  const navigate = useNavigate();
  const sessionIdRef = useRef(getOrCreateSessionId());
  const isFinishingRef = useRef(false);

  // --- Асинхронная инициализация вопросов и сессии ---
  useEffect(() => {
    const loadData = async () => {
      try {
        await initializeQuestions();
        const sessionId = getCurrentSessionId();
        const savedSession = sessionId ? await loadSession(sessionId) : null;

        let questions = getShuffledQuestions();
        let demographics = null;
        let currentQuestionIndex = 0;
        let step = 'demographics';

        if (savedSession) {
          const rawQuestions = getRawQuestionsData();
          if (Array.isArray(savedSession.questions) && savedSession.questions.length > 0) {
            const answeredIds = new Map(savedSession.questions.map(q => [q.id, q.answer]));
            questions = rawQuestions.map(q => ({ ...q, answer: answeredIds.get(q.id) }));
          }
          demographics = savedSession.demographics || null;
          currentQuestionIndex = savedSession.currentQuestionIndex || 0;
          step = savedSession.step === 'test' || (demographics && Object.keys(demographics).length > 0) ? 'test' : 'demographics';
        }

        dispatch({
          type: 'INITIALIZE',
          payload: { questions, demographics, currentQuestionIndex, step }
        });
      } catch (err) {
        console.error('[TestContainer][ERROR]', err);
        dispatch({ type: 'SET_ERROR', payload: 'Не удалось загрузить вопросы. Пожалуйста, обновите страницу.' });
      }
    };
    loadData();
  }, []);

  // Автоматическое сохранение сессии
  useSessionSaver(state);

  const handleAnswer = useCallback((questionId: string, answer: unknown) => {
    dispatch({ type: 'ANSWER_QUESTION', payload: { questionId, answer } });
  }, []);

  const handlePreviousQuestion = useCallback(() => {
    dispatch({ type: 'PREVIOUS_QUESTION' });
  }, []);

  const handleDemographicsSubmit = useCallback((data: Record<string, unknown>) => {
    dispatch({ type: 'SUBMIT_DEMOGRAPHICS', payload: data });
  }, []);

  const handleFinish = useCallback(async () => {
    if (isFinishingRef.current) return; // уже идёт сохранение
    isFinishingRef.current = true;
    dispatch({ type: 'SET_LOADING', payload: true });
    await finishTestFlow({ state, sessionId: sessionIdRef.current, navigate, dispatch });
    isFinishingRef.current = false;
  }, [state, navigate]);

  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).PLAYWRIGHT_E2E) {
      (window as any).handleFinish = handleFinish;
    }
    return () => {
      if (typeof window !== 'undefined' && (window as any).PLAYWRIGHT_E2E) {
        delete (window as any).handleFinish;
      }
    };
  }, [handleFinish]);

  useEffect(() => {
    if (state.step === 'finishing') {
      handleFinish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step]);

  if (state.step === 'loading') {
    return <LoadingDisplay text="Загрузка теста..." />;
  }

  if (state.step === 'error') {
    return <TestErrorDisplay error={state.error ?? ''} />;
  }

  if (state.step === 'demographics') {
    return <DemographicsForm onSubmit={handleDemographicsSubmit} initialData={state.demographics} />;
  }

  if (state.step === 'test' || state.step === 'finishing') {
    if (state.loading) {
      return <LoadingDisplay text="Обрабатываем ваши результаты..." />;
    }
    const currentQuestion = state.questions[state.currentQuestionIndex];
    if (!currentQuestion) {
      return <TestErrorDisplay error="Вопрос не найден. Попробуйте обновить страницу." />;
    }
    const totalQuestions = state.questions.length;
    const currentQuestionNumber = totalQuestions > 0 ? Math.min(state.currentQuestionIndex + 1, totalQuestions) : 0;
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg p-8">
          <ProgressBar current={currentQuestionNumber} total={totalQuestions} />
          <QuestionForm
            question={currentQuestion}
            onAnswer={handleAnswer}
            onPrevious={handlePreviousQuestion}
            response={currentQuestion.answer || null}
          />
        </div>
      </div>
    );
  }

  return <TestErrorDisplay error="Неизвестное состояние приложения." />;
};

export default TestContainer;